import { IUserModel } from '../models';

import { IIsoCountry, IIsoCurrency, IIsoState } from '../interfaces/IAddressInterfaces';
import { COUNTRIES as isoCountries } from '../resources/iso_countries';
import { CURRENCIES as isoCurrencies } from '../resources/iso_currencies';
import { STATES as isoStates } from '../resources/iso_states';

const isoCountriesByAlpha3Code: Record<string, IIsoCountry> = isoCountries as any;
const isoStatesForIsoCountryByAlpha2Code: Record<string, IIsoState[]> = {};
const isoCurrenciesByCurrencyCode: Record<string, IIsoCurrency> = {};
const distinctIsoCurrencies: IIsoCurrency[] = [];
const countryCodes = Object.keys(isoCountriesByAlpha3Code);
const isoCountriesAsArray = countryCodes
  .map((code) => isoCountriesByAlpha3Code[code])
  .sort((a, b) => a.cldr_display_name.localeCompare(b.cldr_display_name));
const USCountryValues = ['US', 'United States'];

const US_OUTLYING_AREAS = ['ASM', 'GUM', 'MNP', 'PRI', 'UMI', 'VIR'];

export function getDisplayCountriesAsArray() {
  return isoCountriesAsArray.filter((country) => !US_OUTLYING_AREAS.includes(country.iso3166alpha3));
}

export function getCountryNameByThreeLetterCode(code: string): string {
  if (!code) {
    return null;
  }
  return isoCountriesByAlpha3Code[code]?.cldr_display_name;
}

export function getCountryTwoLetterCodeByThreeLetterCode(threeLetterCode: string): string {
  const country = getCountryByThreeLetterCode(threeLetterCode);
  return country ? country.iso3166alpha2.toLowerCase() : null;
}

export function getCountryByThreeLetterCode(code: string): IIsoCountry {
  if (!code) {
    return null;
  }
  return isoCountriesByAlpha3Code[code];
}

export function getIsoStateFromUserStateCodeOrName(stateCodeOrName: string): IIsoState | undefined {
  const stateOptions = getIsoStatesForCountryByTwoLetterCode('US');

  let stateOption = stateOptions.find((state) => state.us_state_code === stateCodeOrName?.toUpperCase());

  if (!stateOption) {
    stateOption = stateOptions.find((state) => state.subdivision_name?.toLowerCase() === stateCodeOrName?.toLowerCase());
  }

  return stateOption;
}

/**
 * Translates a US state code or state name to a US state code
 * Useful for a user's address_state field, which should be a state code, but may also contain leftover freeform string data (from before we updated the page)
 * @param stateCodeOrName Ideally a US state code (ex. "IL") or maybe a US state name (ex. "Illinois"), but could also be nothing meaningful (ex. "asdf")
 * @returns The matching US state code, or undefined
 */
export function getUSStateCodeFromUserStateCodeOrName(stateCodeOrName: string): string | undefined {
  const stateOption = getIsoStateFromUserStateCodeOrName(stateCodeOrName);

  return stateOption?.us_state_code;
}

export function getUSStateNameFromUserStateCodeOrName(stateCodeOrName: string): string | undefined {
  const stateOption = getIsoStateFromUserStateCodeOrName(stateCodeOrName);

  return stateOption?.subdivision_name;
}

export function getIsoStatesForCountryByTwoLetterCode(code: string): IIsoState[] {
  if (!code) {
    return null;
  }

  if (!isoStatesForIsoCountryByAlpha2Code || !isoStatesForIsoCountryByAlpha2Code[code]) {
    const statesInCountry = isoStates.filter((state) => state.country_code === code);
    isoStatesForIsoCountryByAlpha2Code[code] = statesInCountry;
  }

  return isoStatesForIsoCountryByAlpha2Code[code];
}

// Previously we were using API to fetch the list of countries from countries.json, which contains different country names for some values
// so if we do not have address_country_code, we need to check by 3 options here
export function findUserCountry(user: IUserModel, country: IIsoCountry): boolean {
  if (user.address_country_code) {
    return user.address_country_code === country.iso3166alpha3;
  }
  const userCountry = user.country;
  return (
    userCountry === country.cldr_display_name ||
    userCountry === country.official_name_en ||
    (country.oldCountryName && userCountry === country.oldCountryName)
  );
}

/**
 * Gets all distinct currencies, this will only return the first currency instance by currency code, e.g. Euro will only have one entry
 */
export function getDistinctCurrencies(): IIsoCurrency[] {
  const isCached = Object.keys(isoCurrenciesByCurrencyCode).length > 0;
  if (isCached) {
    return distinctIsoCurrencies;
  }

  isoCurrencies.forEach(function (currency) {
    // we want to ignore unlisted currencies, and any currencies with an invalid number
    if (!isoCurrenciesByCurrencyCode[currency.alphabetic_code] && currency.numeric_code) {
      isoCurrenciesByCurrencyCode[currency.alphabetic_code] = currency;
      distinctIsoCurrencies.push(currency);
    }
  });

  return distinctIsoCurrencies;
}

export function getStateCodeWithoutCountry(state: IIsoState): string {
  return state.code.replace(`${state.country_code}-`, '');
}

export function isUSCountry(country: string): boolean {
  return country && USCountryValues.includes(country);
}
